/**
 * Offline write-queue for Zenith360 HR.
 *
 * When the app is offline (or a mutation request times out) we push the
 * request into IndexedDB and retry it — in the original order — the moment
 * the browser or Capacitor `Network` plugin reports the connection is back.
 *
 * Every queued item stores:
 *   { id, method, url, body, headers, created_at, retries, last_error }
 *
 * Only *idempotent-safe* mutations (POST/PUT/PATCH/DELETE) are queued. GETs are
 * skipped — the page will simply show cached React state until re-online.
 *
 * The queue is EMBRACED by `api.js` — every axios error caused by a network
 * failure is caught and re-routed through here.
 */

const DB_NAME = "zenith360_sync";
const STORE = "queue";
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode = "readonly") {
  const db = await openDB();
  const t = db.transaction(STORE, mode);
  return t.objectStore(STORE);
}

export async function enqueue(entry) {
  const store = await tx("readwrite");
  const row = {
    ...entry,
    created_at: new Date().toISOString(),
    retries: 0,
  };
  return new Promise((resolve, reject) => {
    const req = store.add(row);
    req.onsuccess = () => { row.id = req.result; broadcast(); resolve(row); };
    req.onerror = () => reject(req.error);
  });
}

export async function all() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function remove(id) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => { broadcast(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

async function update(id, patch) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) return resolve();
      const putReq = store.put({ ...row, ...patch });
      putReq.onsuccess = () => { broadcast(); resolve(); };
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

// ── Broadcast helper — components subscribe to know when queue changes ────
const listeners = new Set();
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function broadcast() { listeners.forEach((f) => { try { f(); } catch (_) { /* ignore */ } }); }

// ── Flush ─────────────────────────────────────────────────────────────────
let flushing = false;
export async function flush(axiosInstance) {
  if (flushing) return { attempted: 0, succeeded: 0, failed: 0 };
  flushing = true;
  let attempted = 0, succeeded = 0, failed = 0;
  try {
    const items = await all();
    // Preserve insertion order (id ascending)
    items.sort((a, b) => a.id - b.id);
    for (const item of items) {
      attempted += 1;
      try {
        await axiosInstance.request({
          method: item.method,
          url: item.url,
          data: item.body,
          headers: item.headers || undefined,
          // Tag so the interceptor doesn't re-queue on failure
          _isReplay: true,
        });
        await remove(item.id);
        succeeded += 1;
      } catch (e) {
        failed += 1;
        await update(item.id, {
          retries: (item.retries || 0) + 1,
          last_error: e?.response?.data?.detail || e?.message || "retry failed",
        });
        // Stop the flush on first failure — likely still offline
        break;
      }
    }
  } finally {
    flushing = false;
  }
  return { attempted, succeeded, failed };
}

export async function queueLength() {
  const items = await all();
  return items.length;
}

// ── Auto-flush hooks ──────────────────────────────────────────────────────
export function installAutoFlush(axiosInstance) {
  const trigger = () => { flush(axiosInstance).catch(() => {}); };
  window.addEventListener("online", trigger);
  window.addEventListener("focus", trigger);
  // Best-effort periodic retry every 45 s
  setInterval(trigger, 45_000);
  // Kick once on start-up
  setTimeout(trigger, 3000);
  // Capacitor `Network` plugin (if available) also emits `capacitor-network-status`
  document.addEventListener("capacitor-network-status", trigger);
}

export function isNetworkError(err) {
  if (!err) return false;
  if (err.code === "ERR_NETWORK") return true;
  if (err.message === "Network Error") return true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  // Axios timeout also treated as offline for our purposes
  if (err.code === "ECONNABORTED") return true;
  return false;
}
